import { getInvoiceThunk, InvoicesStatus, removePromocodeFromInvoiceThunk } from '@spike/invoices-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { useApiClientWrapper, usePermissions } from 'hooks';
import { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './Promocodes.module.scss';
import PromocodeTag from './PromocodeTag';
import AlertMessage from 'components/UI/V3/AlertMessage';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { CalculatingSpinner } from 'components/UI';
import Invoice from '@spike/invoice-model';

interface PromocodesProps {
    invoice: Invoice;
    className?: string;
    onAdd?: () => void;
    onStartRemoving?: () => void;
    onStopRemoving?: () => void;
}

export const Promocodes: FunctionComponent<PromocodesProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const allowApplyDiscounts = permissions.checkout.applyDiscounts;

    const promocodeErrorMessage = useSelector<RootState, string>(state => state.invoices.promocodes.errorMessage);

    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const [errorMessage, setErrorMessage] = useState('');
    const [removing, setRemoving] = useState(false);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.RemovePromocodeFromInvoiceSuccess:
                props.invoice.id && dispatch(getInvoiceThunk(apiClientWrapper, props.invoice.id));
                break;
            case InvoicesStatus.RemovePromocodeFromInvoiceError:
                setErrorMessage(promocodeErrorMessage);
                props.onStopRemoving?.();
                setRemoving(false);
                break;
            case InvoicesStatus.GetInvoiceSuccess:
                props.onStopRemoving?.();
                setRemoving(false);
                break;
            case InvoicesStatus.Error:
                props.onStopRemoving?.();
                setRemoving(false);
        }
    }, [invoicesStatus]);

    const removeHandler = (promocodeId: number) => {
        if (props.invoice.id) {
            props.onStartRemoving?.();
            setRemoving(true);
            dispatch(removePromocodeFromInvoiceThunk(apiClientWrapper, props.invoice.id, promocodeId));
        }
    };

    return (
        <>
            {removing && (
                <div className={styles.spinnerContainer}>
                    <CalculatingSpinner />
                </div>
            )}
            {!removing && errorMessage.trim().length > 0 && (
                <AlertMessage
                    icon={faCircleExclamation}
                    bdColor="#EF4F57"
                    bgColor="#FBB9BC33"
                    iconColor="#EF4F57"
                    className={styles.alertMessage}
                >
                    {errorMessage}
                </AlertMessage>
            )}
            {!removing && (
                <div className={clsx(styles.container, props.className)}>
                    {props.invoice.promocodes.map(promocode => (
                        <PromocodeTag
                            key={promocode.id}
                            id={promocode.id}
                            name={promocode.name}
                            onRemove={() => removeHandler(promocode.id)}
                        />
                    ))}

                    {props.invoice.promocodes.length === 0 && props.onAdd && allowApplyDiscounts && (
                        <div
                            className={styles.addLink}
                            onClick={props.onAdd}
                        >
                            <span className={styles.addLabel}>+ Add Promo Code</span>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Promocodes;
